import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useAdminPrDashboard } from "../../api/hooks/admin";
import AdminController from "../../controllers/AdminController";
import LogoutButton from "../../components/LogoutButton";
import {
  COLUMNS,
  ColId,
  FIELDS,
  Filter,
  GROUPS,
  Group,
  Match,
  Row,
  SECTIONS,
  Section,
  ago,
  buildRows,
  distinctValues,
  filterTest,
  has,
  layout,
  passes,
} from "./prs";
import {
  FIELD_ICONS,
  FILTER_ICON,
  REFRESH_ICON,
  SAVE_ICON,
  SLIDERS_ICON,
  STATUS_ICONS,
} from "./icons";
import "./styles.css";

type Field = (typeof FIELDS)[number];

// a view = filters + display options. Built-in views are tabs you can't delete; yours live in this browser
type ViewState = {
  filters: Filter[];
  match: Match;
  title: string;
  sortCol?: ColId;
  sortDir: number;
  group: Group;
  hidden: ColId[];
  empty: boolean;
};
type View = { id: string; name: string; state: ViewState };

const DISPLAY = {
  sortCol: undefined,
  sortDir: 1,
  group: "sect" as Group,
  hidden: [],
  empty: false,
};
const blank = (filters: Filter[] = []): ViewState => ({
  filters,
  match: "all",
  title: "",
  ...DISPLAY,
});
const builtins = (me: string): View[] => [
  { id: "mine", name: "Your PRs", state: blank([{ col: "author", op: "is", values: [me] }]) },
  {
    id: "review",
    name: "Your review",
    state: blank([{ col: "status", op: "is", values: ["your review", "re-review"] }]),
  },
  {
    id: "involved",
    name: "Involved",
    state: blank([{ col: "involves", op: "is", values: [me] }]),
  },
  { id: "all", name: "All PRs", state: blank() },
];
const VIEW_KEYS: (keyof ViewState)[] = [
  "filters",
  "match",
  "title",
  "sortCol",
  "sortDir",
  "group",
  "hidden",
  "empty",
];
const copy = (s: ViewState): ViewState => JSON.parse(JSON.stringify(s));
const snap = (s: ViewState) => JSON.stringify(VIEW_KEYS.map((k) => s[k] ?? null));

// browser storage can throw (private windows, blocked site data); the page works without it
function load<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}
function store(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* not saved; nothing else to do */
  }
}

const SHORTCUTS: [string, string][] = [
  ["⌘ K", "Command menu and PR search"],
  ["j / ↓", "Next PR"],
  ["k / ↑", "Previous PR"],
  ["Enter / o", "Open PR on GitHub"],
  ["Space", "Preview the PR"],
  ["f", "Add a filter"],
  ["/", "Search titles"],
  ["Shift V", "Display options"],
  ["⌥ Shift G", "Grouping"],
  ["⌥ Shift O", "Ordering"],
  ["⌥ V", "Save view"],
  ["Shift F", "Remove the last filter"],
  ["⌥ Shift F", "Remove all filters"],
  ["G Y", "Go to Your review"],
  ["G R", "Go to Re-review"],
  ["G M", "Go to Your PRs"],
  ["G A", "Go to Author’s turn"],
  ["⇧ ⌘ ,", "Copy the PR link"],
  ["R", "Refresh from GitHub"],
  ["? / ⌘ /", "Show shortcuts"],
  ["Esc", "Close"],
];
const GO: Record<string, Section> = { y: "review", r: "rereview", m: "mine", a: "author" };
const sectName = (id: string) => (SECTIONS.find((s) => s[0] === id) || ["", "None"])[1];

// "j / ↓" = either key; "⇧ ⌘ ," = pressed together
const Keys = ({ k }: { k: string }) => (
  <>
    {k.split(" / ").map((alt, i) => (
      <React.Fragment key={alt}>
        {i > 0 && " or "}
        {alt.split(" ").map((x) => (
          <kbd key={x}>{x}</kbd>
        ))}
      </React.Fragment>
    ))}
  </>
);
const Path = ({ p, l }: { p: string; l: string }) => (
  <>
    <span className="path">{p} ›</span>
    {l}
  </>
);

// main menu (filter fields, command menu, grouping, ordering): type to narrow, ↑/↓ + Enter to pick
type Item = {
  label: string;
  node?: React.ReactNode;
  icon?: React.ReactNode;
  right?: string;
  keys?: string;
  sel?: boolean;
  field?: Field;
  run: () => void;
};
type Menu = { title: string; kbd?: string; items: (query: string) => Item[] };
type Pos = { left: number; top: number };
const below = (r: DOMRect, width: number): Pos => ({
  left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
  top: r.bottom + 4,
});

const PrDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, error, isLoading } = useAdminPrDashboard();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [me, setMe] = useState<string>(() => load("prdash-login", ""));
  const BUILTIN = useMemo(() => builtins(me), [me]);
  const [views, setViews] = useState<View[]>(() => load("prdash-views", []));
  const allViews = BUILTIN.concat(views);
  const [S, setS] = useState<ViewState>(() => ({
    ...blank(),
    ...load<Partial<ViewState>>("prdash-state", BUILTIN[0].state),
  }));
  const [viewId, setViewId] = useState<string | undefined>(() => load("prdash-view", "mine"));
  const curView = allViews.find((v) => v.id === viewId);
  const modified = !curView || snap(curView.state) !== snap(S);
  const update = (patch: Partial<ViewState>) => setS((s) => ({ ...s, ...patch }));

  useEffect(() => store("prdash-login", me), [me]);
  useEffect(() => store("prdash-views", views), [views]);
  useEffect(() => store("prdash-state", S), [S]);
  useEffect(() => store("prdash-view", viewId ?? null), [viewId]);

  const rows = useMemo(() => (data && me ? buildRows(data.prs, me) : []), [data, me]);
  const groups = useMemo(
    () => layout(rows, S.group, S.sortCol, S.sortDir),
    [rows, S.group, S.sortCol, S.sortDir]
  );
  const shown = (r: Row) => passes(r, S.filters, S.match, S.title);
  const visible = groups.flatMap((g) => g.rows).filter(shown);
  const shownCount = visible.length;

  // keyboard cursor: one row at a time, also follows the mouse
  const [cur, setCur] = useState<string | null>(null);
  const curRow = visible.find((r) => r.url === cur);
  const focusRow = (r: Row | undefined, scroll = true) => {
    setCur(r ? r.url : null);
    if (r && scroll)
      document.querySelector(`tr[data-url="${r.url}"]`)?.scrollIntoView({ block: "nearest" });
  };
  const move = (d: number) => {
    const i = visible.findIndex((r) => r.url === cur);
    focusRow(
      i < 0
        ? visible[d > 0 ? 0 : visible.length - 1]
        : visible[Math.min(visible.length - 1, Math.max(0, i + d))]
    );
  };

  const [toastMsg, setToastMsg] = useState("");
  const toastTimer = useRef<number>();
  const toast = (msg: string) => {
    setToastMsg(msg);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToastMsg(""), 1400);
  };
  const copyLink = (r: Row) =>
    navigator.clipboard.writeText(r.url).then(() => toast("Copied PR link"));
  const open = (r: Row) => window.open(r.url, "_blank");
  // skips the server's one-minute copy, so it waits on GitHub (a few seconds)
  const refresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    AdminController.getPrDashboard(true)
      .then((d) => queryClient.setQueryData(["admin-pr-dashboard"], d))
      .catch((e) => toast(`Refresh failed: ${e.message}`))
      .finally(() => setRefreshing(false));
  };

  // the icon a value shows in its column (status ring, CI dot)
  const valIcon = (col: ColId, v: string) => {
    if (col !== "ci" && col !== "status") return null;
    const r = rows.find((r) => has(r, col, v));
    if (!r) return null;
    return col === "ci" ? (
      <span className={`dot ${r.ci.tone}`} />
    ) : (
      <span className={`st ${r.status.icon}`}>{STATUS_ICONS[r.status.icon]}</span>
    );
  };

  // ---- popups: main menu, second-level values menu, display panel, preview, help, saver
  const [menu, setMenu] = useState<{ m: Menu; pos?: Pos } | null>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [sub, setSub] = useState<{ field: Field; pos: Pos } | null>(null);
  const [subQuery, setSubQuery] = useState("");
  const [subActive, setSubActive] = useState(0);
  const [display, setDisplay] = useState<Pos | null>(null);
  const [peek, setPeek] = useState(false);
  const [help, setHelp] = useState(false);
  const [saver, setSaver] = useState(false);
  const [vname, setVname] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const menuIn = useRef<HTMLInputElement>(null);
  const subIn = useRef<HTMLInputElement>(null);
  const searchIn = useRef<HTMLInputElement>(null);

  const closeSub = () => setSub(null);
  const closeMenu = () => {
    setMenu(null);
    setSub(null);
  };
  const closeAll = () => {
    closeMenu();
    setDisplay(null);
  };
  const openMenu = (m: Menu, anchor?: HTMLElement) => {
    closeAll();
    setMenu({ m, pos: anchor ? below(anchor.getBoundingClientRect(), 280) : undefined });
    setQuery("");
    setActive(0);
  };
  const go = (m: Menu) => {
    setMenu((x) => (x ? { ...x, m } : { m }));
    setQuery("");
    setActive(0);
    setSub(null);
  };
  useEffect(() => {
    if (menu) menuIn.current?.focus();
  }, [menu]);

  // beside the main menu (left side if it fits), else under the anchor
  const openSub = (field: Field, anchor: Element | null, focus = true) => {
    if (sub?.field === field) {
      if (focus) subIn.current?.focus();
      return;
    }
    const a = anchor?.getBoundingClientRect();
    const m = menuRef.current?.getBoundingClientRect();
    let pos: Pos = a ? below(a, 290) : { left: 8, top: 80 };
    if (a && m && menuRef.current?.contains(anchor)) {
      const left = m.left - 294 >= 8 ? m.left - 294 : m.right + 4;
      pos = { left, top: Math.max(8, a.top - 44) };
    }
    setSub({ field, pos });
    setSubQuery("");
    setSubActive(0);
    if (focus) setTimeout(() => subIn.current?.focus());
  };

  const toggleValue = (col: ColId, v: string) =>
    setS((s) => {
      const filters = s.filters.map((f) => ({ ...f, values: [...f.values] }));
      let f = filters.find((x) => x.col === col);
      if (!f) {
        f = { col, op: "is", values: [] };
        filters.push(f);
      }
      const i = f.values.findIndex((x) => x.toLowerCase() === v.toLowerCase());
      if (i >= 0) f.values.splice(i, 1);
      else f.values.push(v);
      return { ...s, filters: filters.filter((x) => x.values.length) };
    });

  // second-level menu: one field's values with checkboxes; stays open so you can tick several.
  // Counts = rows you'd see with that value, given your other filters
  const subItems = sub
    ? (() => {
        const col = sub.field.col;
        const now = S.filters.find((x) => x.col === col);
        const base = rows.filter((r) => passes(r, S.filters, S.match, S.title, now));
        const t = subQuery.trim().toLowerCase();
        return distinctValues(rows, col)
          .filter((v) => v.toLowerCase().includes(t))
          .map((v) => ({
            v,
            sel: !!now && now.values.some((x) => x.toLowerCase() === v.toLowerCase()),
            n: base.filter((r) => has(r, col, v)).length,
          }));
      })()
    : [];
  const subOn = Math.max(0, Math.min(subActive, subItems.length - 1));
  const toggleSub = (i: number) => {
    const it = subItems[i];
    if (sub && it) toggleValue(sub.field.col, it.v);
  };

  // F: fields with their values beside them; typing searches values in every field at once
  const filterMain = (): Menu => ({
    title: "Add Filter…",
    kbd: "F",
    items: (q) => {
      const fields: Item[] = FIELDS.map((f) => ({
        label: f.name,
        icon: FIELD_ICONS[f.col],
        right: "›",
        field: f,
        run: () => openSub(f, menuRef.current?.querySelector(".it.on") || null),
      }));
      const t = q.trim().toLowerCase();
      if (!t) return fields;
      const hits = FIELDS.flatMap((f) =>
        distinctValues(rows, f.col)
          .filter((v) => v.toLowerCase().includes(t))
          .map((v): Item => ({
            label: `${f.name} › ${v}`,
            icon: valIcon(f.col, v) || FIELD_ICONS[f.col],
            node: <Path p={f.name} l={v} />,
            run: () => {
              toggleValue(f.col, v);
              closeMenu();
            },
          }))
      );
      return fields.concat(hits.slice(0, 40));
    },
  });

  // ordering: picking the current column again flips the direction
  const sortBy = (c?: ColId) =>
    setS((s) =>
      c === undefined
        ? { ...s, sortCol: undefined, sortDir: 1 }
        : { ...s, sortCol: c, sortDir: s.sortCol === c ? -s.sortDir : 1 }
    );

  // ⌥⇧G / ⌥⇧O, like Linear
  const groupMenu = (): Menu => ({
    title: "Group by…",
    kbd: "⌥⇧G",
    items: () =>
      GROUPS.map(([k, l]) => ({
        label: `Grouping › ${l}`,
        node: <Path p="Grouping" l={l} />,
        sel: S.group === k,
        run: () => {
          closeMenu();
          update({ group: k });
        },
      })),
  });
  const orderMenu = (): Menu => ({
    title: "Order by…",
    kbd: "⌥⇧O",
    items: () =>
      [
        { id: undefined as ColId | undefined, name: "Default" },
        ...COLUMNS.map((c) => ({ id: c.id as ColId | undefined, name: c.name })),
      ].map(({ id, name }) => {
        const on = S.sortCol === id;
        return {
          label: `View ordering › ${name}`,
          node: <Path p="View ordering" l={name} />,
          sel: on,
          right:
            id === undefined
              ? "whose move, then newest"
              : on
                ? `${S.sortDir > 0 ? "↑" : "↓"}  pick again to flip`
                : "",
          run: () => {
            closeMenu();
            sortBy(id);
          },
        };
      }),
  });

  const switchView = (id: string) => {
    const v = allViews.find((v) => v.id === id);
    if (!v) return;
    setS({ ...blank(), ...copy(v.state) });
    setViewId(id);
    closeAll();
  };
  const openSaver = () => {
    closeAll();
    setSaver(true);
    setVname("");
  };
  const custom = curView && !BUILTIN.includes(curView) ? curView : undefined;
  const saveView = (asNew: boolean) => {
    if (!asNew && custom) {
      setViews((vs) => vs.map((v) => (v.id === custom.id ? { ...v, state: copy(S) } : v)));
    } else {
      const name = vname.trim();
      if (!name) return;
      const v = { id: `v${Date.now()}`, name, state: copy(S) };
      setViews((vs) => vs.concat(v));
      setViewId(v.id);
    }
    setSaver(false);
    toast("View saved");
  };
  const deleteView = (id: string) => {
    setViews((vs) => vs.filter((v) => v.id !== id));
    if (viewId === id) setViewId(undefined);
  };
  const clearAll = () => update({ filters: [], title: "" });
  // "Your PRs" filters on your login, so a new login starts you there
  const pickMe = (login: string) => {
    setMe(login);
    setS(copy(builtins(login)[0].state));
    setViewId("mine");
  };

  const goSect = (id: Section) => {
    const r = visible.find((r) => r.sect === id);
    if (!r) return toast(`Nothing in ${sectName(id)}`);
    const h = S.group === "sect" && document.querySelector(`tr.sect[data-g="${id}"]`);
    if (h) h.scrollIntoView({ block: "start" });
    focusRow(r, !h);
  };
  const togglePeek = () => {
    if (peek) return setPeek(false);
    if (!curRow) move(1);
    setPeek(true);
  };
  const toggleDisplay = (anchor: HTMLElement | null) => {
    const wasOpen = !!display;
    closeAll();
    if (wasOpen || !anchor) return;
    const b = anchor.getBoundingClientRect();
    setDisplay({ left: Math.max(8, b.right - 340), top: b.bottom + 4 });
  };
  const dispBtn = useRef<HTMLButtonElement>(null);
  const addBtn = useRef<HTMLButtonElement>(null);

  // ⌘K: every command, plus PR search by number or title once you type
  const cmdMenu = (): Menu => ({
    title: "Type a command or search PRs…",
    items: (q) => {
      const run = (f: () => void) => () => {
        closeMenu();
        f();
      };
      const typed = !!q.trim();
      const cmds: Item[] = [
        { label: "Filter…", keys: "F", run: () => go(filterMain()) },
        ...FIELDS.map((f) => ({
          label: `Filter by ${f.name}…`,
          icon: FIELD_ICONS[f.col],
          field: f,
          run: () => openSub(f, menuRef.current?.querySelector(".it.on") || null),
        })),
        {
          label: "Display options…",
          keys: "⇧ V",
          run: run(() => toggleDisplay(dispBtn.current)),
        },
        {
          label: "Display options › Grouping…",
          node: <Path p="Display options" l="Grouping…" />,
          keys: "⌥ ⇧ G",
          run: () => go(groupMenu()),
        },
        {
          label: "Display options › View ordering…",
          node: <Path p="Display options" l="View ordering…" />,
          keys: "⌥ ⇧ O",
          run: () => go(orderMenu()),
        },
        // once you type, every grouping and ordering choice shows directly
        ...(typed
          ? groupMenu()
              .items("")
              .concat(orderMenu().items(""))
              .map((it) => ({
                ...it,
                label: `Display options › ${it.label}`,
                node: (
                  <>
                    <span className="path">Display options ›</span>
                    {it.node}
                  </>
                ),
              }))
          : []),
        { label: "Save view…", keys: "⌥ V", run: run(openSaver) },
        ...allViews.map((v) => ({
          label: `Open view: ${v.name}`,
          run: run(() => switchView(v.id)),
        })),
        { label: "Remove all filters", keys: "⌥ ⇧ F", run: run(clearAll) },
        { label: "Search titles", keys: "/", run: run(() => searchIn.current?.focus()) },
        ...Object.entries(GO).map(([k, id]) => ({
          label: `Go to ${sectName(id)}`,
          keys: `G ${k.toUpperCase()}`,
          run: run(() => goSect(id)),
        })),
        ...(curRow
          ? [
              { label: "Open PR on GitHub", keys: "↵", run: run(() => open(curRow)) },
              { label: "Preview PR", keys: "Space", run: run(togglePeek) },
              { label: "Copy PR link", keys: "⇧ ⌘ ,", run: run(() => copyLink(curRow)) },
            ]
          : []),
        { label: "Refresh from GitHub", keys: "R", run: run(refresh) },
        { label: "Show keyboard shortcuts", keys: "? / ⌘ /", run: run(() => setHelp(true)) },
      ];
      if (!typed) return cmds;
      // a PR hidden by your filters opens on GitHub; a visible one gets the cursor
      return cmds.concat(
        rows.map((r) => ({
          label: `#${r.number} ${r.title}`,
          right: r.repo,
          run: run(() => (shown(r) ? focusRow(r) : open(r))),
        }))
      );
    },
  });

  const items = menu
    ? menu.m
        .items(query)
        .filter((it) => it.label.toLowerCase().includes(query.trim().toLowerCase()))
    : [];
  const on = Math.max(0, Math.min(active, items.length - 1));
  useEffect(() => {
    menuRef.current?.querySelector(".it.on")?.scrollIntoView({ block: "nearest" });
  }, [on, menu]);

  // one document-level key handler that always sees the latest state
  const gAt = useRef(0);
  const onKey = useRef<(e: KeyboardEvent) => void>(() => undefined);
  onKey.current = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    const typing = target.matches("input, textarea, select");
    if (e.key === "Escape") {
      if (sub) {
        closeSub();
        if (menu) menuIn.current?.focus();
      } else if (menu) closeMenu();
      else if (display) closeAll();
      else if (saver) setSaver(false);
      else if (help) setHelp(false);
      else if (peek) setPeek(false);
      else if (typing) target.blur();
      return;
    }
    if (e.metaKey && e.key === "k") {
      e.preventDefault();
      if (menu) closeMenu();
      else openMenu(cmdMenu());
      return;
    }
    if (e.metaKey && e.key === "/") {
      e.preventDefault();
      setHelp((h) => !h);
      return;
    }
    if (typing || e.ctrlKey || !me) return;
    if (e.metaKey) {
      if (e.shiftKey && e.code === "Comma" && curRow) {
        e.preventDefault();
        copyLink(curRow);
      }
      return;
    }
    // e.code: Option changes e.key on macOS
    if (e.code === "KeyF" && e.shiftKey) {
      e.preventDefault();
      if (e.altKey) clearAll();
      else update({ filters: S.filters.slice(0, -1) });
      return;
    }
    if (e.code === "KeyV" && e.altKey) {
      e.preventDefault();
      openSaver();
      return;
    }
    if (e.code === "KeyG" && e.altKey && e.shiftKey) {
      e.preventDefault();
      openMenu(groupMenu());
      return;
    }
    if (e.code === "KeyO" && e.altKey && e.shiftKey) {
      e.preventDefault();
      openMenu(orderMenu());
      return;
    }
    if (e.altKey) return;
    if (e.code === "KeyV" && e.shiftKey) {
      e.preventDefault();
      toggleDisplay(dispBtn.current);
      return;
    }
    const k = e.key;
    // G then a letter within a second jumps to a section
    if (Date.now() - gAt.current < 1000 && GO[k]) {
      gAt.current = 0;
      goSect(GO[k]);
      return;
    }
    if (k === "g") {
      gAt.current = Date.now();
      return;
    }
    if (k === "j" || k === "ArrowDown") {
      e.preventDefault();
      move(1);
    } else if (k === "k" || k === "ArrowUp") {
      e.preventDefault();
      move(-1);
    } else if ((k === "Enter" || k === "o") && curRow) open(curRow);
    else if (k === " ") {
      e.preventDefault();
      togglePeek();
    } else if (k === "f") {
      e.preventDefault();
      openMenu(filterMain(), addBtn.current || undefined);
    } else if (k === "/") {
      e.preventDefault();
      searchIn.current?.focus();
      searchIn.current?.select();
    } else if (k === "r") refresh();
    else if (k === "?") setHelp((h) => !h);
  };
  useEffect(() => {
    const f = (e: KeyboardEvent) => onKey.current(e);
    document.addEventListener("keydown", f);
    return () => document.removeEventListener("keydown", f);
  }, []);

  // clicking outside a popup closes it
  useEffect(() => {
    const f = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".pop, .trigger")) {
        setMenu(null);
        setSub(null);
        setDisplay(null);
      }
    };
    document.addEventListener("mousedown", f);
    return () => document.removeEventListener("mousedown", f);
  }, []);

  const onMenuKey = (e: React.KeyboardEvent) => {
    const it = items[on];
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive(on + (e.key === "ArrowDown" ? 1 : -1));
      closeSub();
    } else if (e.key === "Enter") {
      e.preventDefault();
      it?.run();
    } else if (e.key === "ArrowRight" && it?.field) {
      e.preventDefault();
      openSub(it.field, menuRef.current?.querySelector(".it.on") || null);
    }
  };
  const onSubKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setSubActive(subOn + (e.key === "ArrowDown" ? 1 : -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      toggleSub(subOn);
    } else if (e.key === "ArrowLeft" && !subQuery && menu) {
      e.preventDefault();
      closeSub();
      menuIn.current?.focus();
    }
  };

  const opText = (f: Filter) =>
    f.op === "not"
      ? f.values.length > 1
        ? "is none of"
        : "is not"
      : f.values.length > 1
        ? "is any of"
        : "is";
  const cols = COLUMNS.filter((c) => !S.hidden.includes(c.id));

  const cell = (r: Row, c: ColId) => {
    switch (c) {
      case "ci":
        return (
          <td key={c} className="ci" title={`CI ${r.ci.word}`}>
            <span className={`dot ${r.ci.tone}`} />
          </td>
        );
      case "repo":
        return <td key={c}>{r.repo}</td>;
      case "number":
        return (
          <td key={c} className="num">
            <a href={r.url} target="_blank" rel="noreferrer">
              #{r.number}
            </a>
          </td>
        );
      case "author":
        return <td key={c}>@{r.author}</td>;
      case "reviewer":
        return (
          <td key={c} className="revs">
            {r.reviewers.map((w) => `@${w}`).join(", ")}
          </td>
        );
      case "title":
        return (
          <td key={c} className="title">
            {r.ticket && <span className="tk">{r.ticket}</span>}
            <a href={r.url} target="_blank" rel="noreferrer">
              {r.title}
            </a>
          </td>
        );
      case "status":
        return (
          <td key={c}>
            <span className={`st ${r.status.icon}`}>
              {STATUS_ICONS[r.status.icon]}
              {r.status.label}
            </span>
          </td>
        );
      case "age":
        return (
          <td key={c} className={`num ${r.ageDays > 14 ? "stale" : r.ageDays > 5 ? "aging" : ""}`}>
            {ago(r.ageDays)}
          </td>
        );
      case "draft":
        return <td key={c}>{r.draft ? "Draft" : "—"}</td>;
    }
  };

  const header = (
    <div className="nav">
      <button className="btn" onClick={() => navigate("/")}>
        Back to Home
      </button>
      <LogoutButton />
    </div>
  );

  if (!me || (data && !data.members.some((m) => m.login === me))) {
    return (
      <div className="prd">
        <main>
          {header}
          <div className="who">
            <h1>Who are you on GitHub?</h1>
            <p>Your PRs and the ones waiting on your review are worked out from this.</p>
            {!data && <p>{error ? String(error) : "loading…"}</p>}
            <div className="pills">
              {data?.members.map((m) => (
                <button key={m.login} className="pill" onClick={() => pickMe(m.login)}>
                  {m.name ? `${m.name} (@${m.login})` : `@${m.login}`}
                </button>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  const peekRow = curRow;
  return (
    <div className="prd">
      <main>
        {header}
        <div className="top">
          <span className="live" />
          <h1>Pull requests</h1>
          <div className="tabs">
            {allViews.map((v) => (
              <button
                key={v.id}
                className={`tab${v.id === viewId ? ` on${modified ? " mod" : ""}` : ""}`}
                title={v.id === viewId && modified ? "Changed since saved" : ""}
                onClick={(e) =>
                  (e.target as HTMLElement).closest(".del") ? deleteView(v.id) : switchView(v.id)
                }
              >
                {v.name}
                {!BUILTIN.includes(v) && (
                  <span className="del" title="Delete view">
                    ✕
                  </span>
                )}
              </button>
            ))}
          </div>
          <span className="stamp">
            {data && `GitHub as of ${new Date(data.fetchedAt * 1000).toLocaleTimeString()}`}
          </span>
          <select
            className="me"
            value={me}
            title="Who you are on GitHub"
            onChange={(e) => pickMe(e.target.value)}
          >
            {data?.members.map((m) => (
              <option key={m.login} value={m.login}>
                @{m.login}
              </option>
            ))}
          </select>
          <input
            ref={searchIn}
            className="q"
            placeholder="Search titles   /"
            autoComplete="off"
            spellCheck={false}
            value={S.title}
            onChange={(e) => update({ title: e.target.value })}
          />
          <button
            ref={addBtn}
            className="ibtn trigger"
            title="Filter   F"
            onClick={(e) => openMenu(filterMain(), e.currentTarget)}
          >
            {FILTER_ICON}
          </button>
          <button
            ref={dispBtn}
            className={`ibtn trigger${display ? " on" : ""}`}
            title="Display options   ⇧V"
            onClick={(e) => toggleDisplay(e.currentTarget)}
          >
            {SLIDERS_ICON}
          </button>
          {modified && (
            <button className="ibtn" title="Save view   ⌥V" onClick={openSaver}>
              {SAVE_ICON}
            </button>
          )}
          <button
            className={`ibtn${refreshing ? " spin" : ""}`}
            title="Refresh from GitHub   R"
            disabled={refreshing}
            onClick={refresh}
          >
            {REFRESH_ICON}
          </button>
          <button className="ibtn" title="Keyboard shortcuts   ?" onClick={() => setHelp(!help)}>
            <kbd>?</kbd>
          </button>
        </div>

        {saver && (
          <div className="saver">
            <input
              autoFocus
              placeholder="View name"
              autoComplete="off"
              spellCheck={false}
              value={vname}
              onChange={(e) => setVname(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveView(true)}
            />
            <button className="btn ghost" onClick={() => setSaver(false)}>
              Cancel
            </button>
            {custom && (
              <button className="btn" onClick={() => saveView(false)}>
                Update “{custom.name}”
              </button>
            )}
            <button className="btn primary" onClick={() => saveView(true)}>
              Save as new view
            </button>
          </div>
        )}

        {/* filter bar: chips read [icon Field][is][value][×]; click the "is" or the value to change it */}
        <div className="bar">
          {S.filters.map((f, i) => {
            const fd = FIELDS.find((x) => x.col === f.col);
            if (!fd) return null;
            const setF = (patch: Partial<Filter> | null) =>
              update({
                filters: S.filters.flatMap((x, j) =>
                  j !== i ? [x] : patch ? [{ ...x, ...patch }] : []
                ),
              });
            return (
              <span className="chip" key={f.col}>
                <span className="k">
                  {FIELD_ICONS[f.col]}
                  {fd.name}
                </span>
                <span
                  className="op"
                  title="Switch is / is not"
                  onClick={() => setF({ op: f.op === "not" ? "is" : "not" })}
                >
                  {opText(f)}
                </span>
                <span
                  className="v trigger"
                  title="Change values"
                  onClick={(e) => {
                    closeAll();
                    openSub(fd, e.currentTarget);
                  }}
                >
                  {f.values.length > 1 ? (
                    `${f.values.length} ${fd.many}`
                  ) : (
                    <>
                      {valIcon(f.col, f.values[0])}
                      {f.values[0]}
                    </>
                  )}
                  {/* each chip's count is that filter on its own */}
                  <span className="n">{rows.filter(filterTest(f)).length}</span>
                </span>
                <button className="x" title="Remove filter" onClick={() => setF(null)}>
                  ✕
                </button>
              </span>
            );
          })}
          <button
            className="btn ghost trigger"
            title="Add filter   F"
            onClick={(e) => openMenu(filterMain(), e.currentTarget)}
          >
            {FILTER_ICON}
            {!S.filters.length && " Filter"}
          </button>
          <span className="sp" />
          {S.filters.length > 1 && (
            <button
              className="btn ghost"
              title="Switch between all and any"
              onClick={() => update({ match: S.match === "all" ? "any" : "all" })}
            >
              Match <b>{S.match}</b> {S.match === "all" ? "filters" : "filter"}
            </button>
          )}
          {(S.filters.length > 0 || S.title.trim()) && (
            <button className="btn ghost" onClick={clearAll}>
              Clear
            </button>
          )}
          {modified && (
            <button className="btn" title="Save view   ⌥V" onClick={openSaver}>
              Save
            </button>
          )}
          <span className="showing">{rows.length ? `${shownCount} / ${rows.length}` : ""}</span>
        </div>

        {!!error && data && <p className="err">GitHub refresh failed: {String(error)}</p>}
        {/* only the list scrolls; the header and filter bar stay put */}
        <div className="scroll">
          <table className="grid">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.id} className={c.num ? "num" : ""} onClick={() => sortBy(c.id)}>
                    {c.name}
                    {S.sortCol === c.id && (
                      <span className="arrow">{S.sortDir > 0 ? " ↑" : " ↓"}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={cols.length} className="empty">
                    loading…
                  </td>
                </tr>
              )}
              {!data && !!error && (
                <tr>
                  <td colSpan={cols.length} className="err">
                    {String(error)}
                  </td>
                </tr>
              )}
              {groups.map((g) => {
                const vis = g.rows.filter(shown);
                const showHead =
                  S.group !== "none" && (vis.length > 0 || (S.empty && S.group === "sect"));
                return (
                  <React.Fragment key={g.key}>
                    {showHead && (
                      <tr className="sect" data-g={g.key}>
                        <td colSpan={cols.length}>
                          <span className="gl">
                            {S.group === "sect" ? (
                              sectName(g.key)
                            ) : (
                              <>
                                {valIcon(S.group as ColId, g.key)}
                                {g.key || "None"}
                              </>
                            )}
                          </span>
                          <span className="n">{vis.length}</span>
                        </td>
                      </tr>
                    )}
                    {vis.map((r) => (
                      <tr
                        key={r.url}
                        data-url={r.url}
                        className={`${r.ticket ? `grp grp-${r.gpos}` : ""}${r.url === cur ? " cur" : ""}`}
                        onMouseMove={() => r.url !== cur && setCur(r.url)}
                      >
                        {cols.map((c) => cell(r, c.id))}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {rows.length > 0 && shownCount === 0 && (
            <p className="empty">
              No PRs match these filters.{" "}
              <button className="btn ghost" onClick={clearAll}>
                Clear filters
              </button>
            </p>
          )}
        </div>
      </main>

      {menu && (
        <div
          ref={menuRef}
          className={`menu pop${menu.pos ? "" : " center"}`}
          style={menu.pos ? { left: menu.pos.left, top: menu.pos.top } : undefined}
        >
          <div className="head">
            <input
              ref={menuIn}
              placeholder={menu.m.title}
              autoComplete="off"
              spellCheck={false}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
                closeSub();
              }}
              onKeyDown={onMenuKey}
            />
            {menu.m.kbd && <kbd>{menu.m.kbd}</kbd>}
          </div>
          <div className="list">
            {items.map((it, i) => (
              <div
                key={it.label}
                className={`it${i === on ? " on" : ""}`}
                onMouseMove={(e) => {
                  if (i !== on) setActive(i);
                  // hovering a field opens its values beside the menu, like Linear
                  if (it.field) openSub(it.field, e.currentTarget, false);
                  else if (sub) closeSub();
                }}
                onClick={() => it.run()}
              >
                <span className="lb">
                  {it.icon && <span className="ic">{it.icon}</span>}
                  {it.node ?? it.label}
                </span>
                <span className="n">
                  {it.keys ? <Keys k={it.keys} /> : it.right || (it.sel ? "✓" : "")}
                </span>
              </div>
            ))}
            {!items.length && <div className="it">No matches</div>}
          </div>
        </div>
      )}

      {sub && (
        <div className="menu pop sub" style={{ left: sub.pos.left, top: sub.pos.top }}>
          <div className="head">
            <input
              ref={subIn}
              placeholder="Filter…"
              autoComplete="off"
              spellCheck={false}
              value={subQuery}
              onChange={(e) => {
                setSubQuery(e.target.value);
                setSubActive(0);
              }}
              onKeyDown={onSubKey}
            />
          </div>
          <div className="list">
            {subItems.map((it, i) => (
              <div
                key={it.v}
                className={`it${i === subOn ? " on" : ""}${it.n ? "" : " zero"}`}
                onMouseMove={() => i !== subOn && setSubActive(i)}
                onClick={() => toggleSub(i)}
              >
                <span className="lb">
                  <span className={`cb${it.sel ? " on" : ""}`}>✓</span>
                  {valIcon(sub.field.col, it.v)}
                  {it.v}
                </span>
                <span className="n">{it.n}</span>
              </div>
            ))}
            {!subItems.length && <div className="it">No matches</div>}
          </div>
        </div>
      )}

      {/* Display options (Shift V): grouping, ordering, empty groups, which columns show */}
      {display && (
        <div className="panel pop" style={{ left: display.left, top: display.top }}>
          <div className="row">
            <span>Grouping</span>
            <select value={S.group} onChange={(e) => update({ group: e.target.value as Group })}>
              {GROUPS.map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div className="row">
            <span>Ordering</span>
            <span className="ctl">
              <button
                className="ibtn"
                title="Flip direction"
                disabled={S.sortCol === undefined}
                onClick={() => update({ sortDir: -S.sortDir })}
              >
                {S.sortDir > 0 ? "↑" : "↓"}
              </button>
              <select
                value={S.sortCol ?? ""}
                onChange={(e) =>
                  update({
                    sortCol: (e.target.value || undefined) as ColId | undefined,
                    sortDir: 1,
                  })
                }
              >
                <option value="">Default (whose move, newest)</option>
                {COLUMNS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </span>
          </div>
          <hr />
          <div className="row">
            <span>Show empty groups</span>
            <button
              className={`switch${S.empty ? " on" : ""}`}
              title="Only for Whose move grouping"
              onClick={() => update({ empty: !S.empty })}
            />
          </div>
          <hr />
          <div className="lbl">Display properties</div>
          <div className="pills">
            {COLUMNS.filter((c) => c.id !== "title").map((c) => (
              <button
                key={c.id}
                className={`pill${S.hidden.includes(c.id) ? " off" : ""}`}
                onClick={() =>
                  update({
                    hidden: S.hidden.includes(c.id)
                      ? S.hidden.filter((x) => x !== c.id)
                      : S.hidden.concat(c.id),
                  })
                }
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="foot">
            <button className="btn ghost" onClick={() => update({ ...DISPLAY })}>
              Reset display
            </button>
          </div>
        </div>
      )}

      {peek && (
        <aside className="peek">
          {!peekRow ? (
            <p className="empty">
              No PR selected. Use <kbd>j</kbd> / <kbd>k</kbd>.
            </p>
          ) : (
            <>
              <div className="id">
                {peekRow.repo} #{peekRow.number}
                {peekRow.ticket && ` · ${peekRow.ticket}`}
              </div>
              <h3>
                <a href={peekRow.url} target="_blank" rel="noreferrer">
                  {peekRow.title}
                </a>
              </h3>
              <dl>
                <dt>Status</dt>
                <dd>
                  <span className={`st ${peekRow.status.icon}`}>
                    {STATUS_ICONS[peekRow.status.icon]}
                    {peekRow.status.label}
                  </span>
                </dd>
                <dt>CI</dt>
                <dd>
                  <span className={`dot ${peekRow.ci.tone}`} /> {peekRow.ci.word}
                </dd>
                <dt>Author</dt>
                <dd>@{peekRow.author}</dd>
                <dt>Reviewers</dt>
                <dd>{peekRow.reviewers.map((w) => `@${w}`).join(", ") || "—"}</dd>
                <dt>Updated</dt>
                <dd>{ago(peekRow.ageDays)} ago</dd>
                <dt>Draft</dt>
                <dd>{peekRow.draft ? "Yes" : "No"}</dd>
              </dl>
              <div className="links">
                <a className="btn" href={peekRow.url} target="_blank" rel="noreferrer">
                  Open <kbd>↵</kbd>
                </a>
                <a className="btn" href={`${peekRow.url}/files`} target="_blank" rel="noreferrer">
                  Files changed
                </a>
                <a className="btn" href={`${peekRow.url}/checks`} target="_blank" rel="noreferrer">
                  Checks
                </a>
              </div>
            </>
          )}
          <p className="hint">
            <kbd>j</kbd> <kbd>k</kbd> next / previous · <kbd>Space</kbd> or <kbd>Esc</kbd> to close
          </p>
        </aside>
      )}

      {help && (
        <div className="help" onClick={(e) => e.target === e.currentTarget && setHelp(false)}>
          <div className="card">
            <h3>Keyboard shortcuts</h3>
            <dl>
              {SHORTCUTS.map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt>
                    <Keys k={k} />
                  </dt>
                  <dd>{v}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        </div>
      )}
      {toastMsg && <div className="toast">{toastMsg}</div>}
    </div>
  );
};

export default PrDashboardPage;
